import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import { useUserStore } from '@caradasstore/UserStore'
import { extractApiError, FormModal } from '@components/branded-modal'
import {
  AdasBox,
  AdasCheckbox,
  AdasFormControl,
  AdasFormControlLabel,
  AdasSelect,
  AdasTypography,
  AdsCustomModal
} from '@components/wrapper-components'
import { Calendar, FileText as PdfIcon, Plus, CalendarX, ChevronDown, PenLine, ExternalLink, Loader2, Info } from 'lucide-react'
import {
  cancelAppointment,
  createAppointmentEstimate,
  getAppointmentDetails,
  getAppointmentEstimate,
  getAppointmentLinkedRecords,
  getAssignee,
  getQuoteByReferenceNumber,
  getReferralList,
  requestVinDataForQuotesWOInvoicesByVin,
  transformToWO,
  updateAppointment,
  updateSchedularAssignee,
  updateSchedularStatus
} from 'api/api'
import { DATE_FORMATS, REDIRECT_URLS, MAX_FILE_SIZE_MB, getWorkOrderSummaryUrl } from 'constants'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { Logger } from 'logger'
import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import theme from 'theme/theme'
import { scrollPageTop } from 'utils/useScrollTop'
import { formatDate, formatExistingTimeOrReturn, validations, formatPhoneNumber } from 'utils/utils'
import { adaptRecordRef } from '@pages/adas-intelligence/adapters'
import { AppointmentForm } from './AppointmentForm'
import { AppointmentPayload } from './AppointmentPayload'
import { schedularStatusStyles, statusOptions } from './helper'
import isEqual from 'lodash/isEqual'
import { AppointmentAttachment } from './AppointmentAttachment'
import { DataOverrideCard } from './DataOverrideCard'
import { useDeviceQueries } from '@hooks/useDeviceQueries'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { sortBy } from 'lodash'
dayjs.extend(utc)

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png'
]

const FOOTER_BUTTON_CLASS =
  'inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[#D1D5D8] bg-white px-4 py-2 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'

const VIN_RECORD_STAGES = {
  quote: { stage: 'QUOTE', path: '/quotes' },
  workorder: { stage: 'WORKORDER', path: '/workorders' },
  invoice: { stage: 'INVOICE', path: '/invoices' }
}

export const AppointmentDetails = ({ isOpen = true, selectedEventId, cancel, setRefresh, initialEditMode = false }) => {
  // Client users manage their own appointments here (2026-10-23): assignee, status, edit and delete. They never
  // create a work order (CAR-3969) - no New Work Order screen and no converting the appointment's quote.
  const isReadOnly = useIsReadOnly()
  const [editMode, setEditMode] = useState(false)
  const [open, setOpen] = useState(false)
  const [cancelAppointmentModal, setCancelAppointmentModal] = useState(false)
  const [apiError, setApiError] = useState(null)
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const addMessage = useMessageStore((store) => store.addMessage)
  const isLoading = useLoadingStore((store) => store.loading)
  const organisationDetail = useOrganisationStore((store) => store.organisationDetail)
  const history = useHistory()
  const currentUser = useUserStore((store) => store.currentUser)

  const [formValues, setFormValues] = useState({ recentEstimate: null, calibrationFile: null })
  const [formEditValues, setFormEditValues] = useState({ recentEstimate: null })
  // Quotes/Work Orders/Invoices that already exist for this appointment. When any exist we
  // link to them instead of offering Create Quote / Create Work Order. Each entry is resolved
  // via the shared adaptRecordRef helper ({ stage, number, href }) so labels + routes stay
  // consistent with the AI report summary page (EstimateDetailPage).
  const [linkedRecords, setLinkedRecords] = useState([])
  // Gates the modal body/footer until the appointment details and the existing-records check
  // have loaded. Without this, the footer briefly renders the Create Quote / Create Work Order
  // branch before the check resolves, causing a flash before the links replace it.
  const [isDetailsLoading, setIsDetailsLoading] = useState(true)
  const [availabilities, setAvailabilities] = useState({
    overrides_date: '', // You can provide a default date or empty string
    slots: [] // Default empty array for slots
  })

  const [formErrors, setFormErrors] = useState({
    recentEstimate: ''
    // ... other error fields
  })
  const { isMobile } = useDeviceQueries()
  const [formattedDate, setFormattedDate] = useState('')
  const [newTime, setNewTime] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true) // New state for submit button
  const [referralSourceList, setReferralSourceList] = useState([])

  const [formValid, setFormValid] = useState({
    fullName: true,
    primaryEmail: true,
    secondaryEmail: true,
    phone: true,
    businessName: true,
    businessPhone: true,
    aro: true
  })

  const validationMap = {
    fullName: validations.requiredAlphanumericText,
    primaryEmail: validations.email,
    secondaryEmail: validations.optionalEmail,
    businessPhone: validations.optionalPhone,
    phone: validations.optionalPhone,
    aro: validations.alphanumericText
  }

  const hasChanges = () => {
    const { recentEstimate, businessId, useExistingClient, ...requiredFields } = formValues
    const {
      recentEstimate: originalEstimate,
      businessId: originalBussinessId,
      useExistingClient: duseExistingClient,
      ...originalFormValues
    } = formEditValues
    return !isEqual(requiredFields, originalFormValues) || !isEqual(recentEstimate?.id, originalEstimate?.id)
  }

  useEffect(() => {
    const areRequiredFieldsFilled = () => {
      const { fullName, primaryEmail, appointmentDate, appointmentTime, vehicleYear, vehicleMake, vehicleModel, businessPhone } = formValues
      return fullName && primaryEmail && appointmentDate && appointmentTime && vehicleYear && vehicleMake && vehicleModel && businessPhone
    }

    const areAllFieldsValid = () => {
      const { fullName, primaryEmail, secondaryEmail, businessPhone, aro } = formValid

      return fullName && primaryEmail && secondaryEmail && aro && businessPhone
    }

    const isFormValid = () => {
      return areRequiredFieldsFilled() && areAllFieldsValid()
    }

    const canDisableSubmit = isLoading || !isFormValid() || (!hasChanges() && editMode)
    setIsSubmitDisabled(canDisableSubmit)
  }, [formValues, editMode])
  const [assigneeOptions, setAssigneeOptions] = useState([])

  useEffect(() => {
    fetchAssignees()
    fetchAppointmentDetails()
  }, [ccId])

  const fetchAssignees = async () => {
    try {
      const response = await getAssignee(ccId, organisationDetail.id)
      const formattedAssignees =
        response?.data?.map((assignee) => ({
          id: assignee.id,
          name: assignee.full_name
        })) || []
      const sortedAssignees = sortBy(formattedAssignees, 'name')
      const defaultAssignee = { id: 'null', name: 'Unassigned' }
      setAssigneeOptions([defaultAssignee, ...sortedAssignees])
    } catch (error) {
      console.error('Error fetching assignees:', error)
    }
  }

  const fetchAppointmentDetails = async () => {
    try {
      const [appointmentDetailsResponse, appointmentEstimateResponse, linkedRecordsResponse] = await Promise.all([
        getAppointmentDetails(ccId, selectedEventId),
        getAppointmentEstimate(ccId, selectedEventId),
        // A failed check must not block the modal; fall back to the details' linked_qwi below.
        getAppointmentLinkedRecords(ccId, selectedEventId).catch(() => null)
      ])
      const appointmentData = appointmentDetailsResponse?.data
      const timezoneOffset = appointmentData?.organisation.timezone_at || 'UTC' // Assuming the value is in format "-09:00"
      const { newDate, newTime, startTime, endTime, newEndTime } = formatExistingTimeOrReturn(
        appointmentData.appointment_date,
        appointmentData.start_time,
        timezoneOffset,
        appointmentData.duration
      )

      const fdate = dayjs(newDate).format(DATE_FORMATS.ISO_DATE_STRING)
      setFormattedDate(fdate)
      setNewTime(newTime)
      setStartTime(startTime)
      setEndTime(endTime)
      // Map fetched data to form values

      let recentEstimate = appointmentEstimateResponse?.data?.find((item) => item.type === 'ESTIMATE')
      let calibrationFile = appointmentEstimateResponse?.data?.find((item) => item.type !== 'ESTIMATE')

      // Existing Quotes/Work Orders/Invoices for the appointment. Prefer the dedicated
      // linked-records check; if it failed, fall back to the single linked_qwi on the details.
      const linkedQwi = appointmentData?.linked_qwi || appointmentData?.linkedQwi || appointmentData?.qwi
      const linkedQwis = Array.isArray(linkedRecordsResponse?.data) ? linkedRecordsResponse.data : linkedQwi ? [linkedQwi] : []
      // Keep the quote's internal id so Create Work Order can transform it in place.
      const records = linkedQwis
        .map((qwi) => {
          const record = adaptRecordRef({ linked_qwi: qwi })
          return record?.stage === 'QUOTE' ? { ...record, id: qwi?.quote_id || qwi?.quoteId } : record
        })
        .filter((record) => record?.href)

      const vin = appointmentData?.vehicle?.vin
      if (vin) {
        const vinResponse = await requestVinDataForQuotesWOInvoicesByVin({ vin, ccid: ccId }).catch(() => null)
        const vinRecords = (Array.isArray(vinResponse?.data) ? vinResponse.data : [])
          .filter((row) => row?.reference_number)
          .map(
            (row) => VIN_RECORD_STAGES[row.table_name] && { ...VIN_RECORD_STAGES[row.table_name], number: row.reference_number, id: row.id }
          )
          .filter(Boolean)
          .map((record) => ({ ...record, href: `${record.path}/${record.number}/summary` }))
        records.push(...vinRecords)
      }
      setLinkedRecords(records.filter((record, index) => records.findIndex((r) => r.href === record.href) === index))

      const mappedData = {
        appointment_id: appointmentData?.id,
        referenceNo: appointmentData?.reference_no,
        assignee: appointmentData?.assignee?.is_active ? appointmentData?.assignee?.id : 'null' || 'null',
        clientId: appointmentData.client?.id,
        vehicleId: appointmentData.vehicle?.id,
        appointmentDate: fdate || '',
        appointmentTime: newTime || '',
        appointmentEndTime: newEndTime || '',
        fullName: appointmentData.client?.full_name || '',
        businessName: appointmentData.client?.business_name || '',
        businessPhone: appointmentData.client?.business_phone || '',
        primaryEmail: appointmentData.client?.primary_email || '',
        secondaryEmail: appointmentData.client?.secondary_email || '',
        referral: appointmentData.client?.referral_source || '',
        address: appointmentData.client?.address || '',
        phone: appointmentData.client?.phone || '',
        state: appointmentData.client?.state || '',
        city: appointmentData.client?.city || '',
        zipCode: appointmentData.client?.zip || '',
        useVin: appointmentData.vehicle?.vin ? true : false,
        vin: appointmentData.vehicle?.vin || '',
        vehicleYear: appointmentData.vehicle?.year || '',
        vehicleMake: appointmentData.vehicle?.make || '',
        vehicleModel: appointmentData.vehicle?.model || '',
        status: appointmentData.status || '',
        notes: appointmentData.notes || '',
        pickDeliver: appointmentData.delivery_pickup ? true : false,
        aro: appointmentData.vehicle.repair_order_num || '',
        timezone_at: appointmentData.timezone_at,
        useExistingClient: !!appointmentData.client?.main_client_id,
        duration: appointmentData.duration,
        businessId: appointmentData.client?.main_client_id || '',
        recentEstimate: recentEstimate,
        calibrationFile: calibrationFile
      }
      setFormValues(mappedData)
      setFormEditValues(mappedData)
      // CAR-4034 — Edit from a quote's existing-appointment dialog opens straight into the edit form,
      // once its values are loaded.
      if (initialEditMode) setEditMode(true)
    } catch (error) {
      console.error('Error fetching appointment details:', error)
      handleCustomError(error)
    } finally {
      setIsDetailsLoading(false)
    }
  }

  const handleChange = (eventOrValue) => {
    if (eventOrValue && eventOrValue.target) {
      const { name, value, type, checked, files } = eventOrValue.target
      if (name === 'recentEstimate' && files) {
        const selectedFile = files[0]
        if (!ALLOWED_FILE_TYPES.includes(selectedFile.type)) {
          addMessage({
            text: 'Invalid file type. Please upload a PDF, DOCX, JPG, or PNG file.'
          })
          return
        }
        if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          addMessage({
            text: `File size exceeds the limit of ${MAX_FILE_SIZE_MB}MB.`
          })
          return
        }
        if (selectedFile.name?.length > 80) {
          addMessage({
            text: `The file name cannot exceed more than 80 characters.`
          })
          return
        }
        setFormValues((prev) => ({
          ...prev,
          recentEstimate: selectedFile
        }))
        if (validationMap[name]) {
          setFormValid((prev) => ({
            ...prev,
            [name]: validationMap[name](value.trim())
          }))
        }
      } else {
        if (validationMap[name]) {
          setFormValid((prev) => ({
            ...prev,
            [name]: validationMap[name](value.trim())
          }))
        }
        setFormValues((prev) => {
          const updatedValues = {
            ...prev,
            [name]: type === 'checkbox' ? checked : value
          }

          if (name === 'appointmentTime') {
            updatedValues.appointmentEndTime = ''
          }

          return updatedValues
        })
      }
    } else if (eventOrValue) {
      let newAppointmentTime = formEditValues.appointmentTime
      if (eventOrValue !== formEditValues.appointmentDate) {
        newAppointmentTime = null
      }

      // Update the form values
      setFormValues((prev) => ({
        ...prev,
        appointmentTime: newAppointmentTime,
        appointmentDate: eventOrValue
      }))
    }
  }

  const redirectToNewWorkOrder = () => {
    history.push(REDIRECT_URLS.NEW_WORK_ORDER, { ...formValues })
  }
  // A quote already exists for the vehicle: convert it to a work order directly (same API as
  // the Quote summary page) instead of opening the New Work Order screen. The work order
  // inherits the quote's reference number, so we can route straight to its summary.
  const [convertingQuote, setConvertingQuote] = useState(null)
  const convertQuoteToWorkOrder = async (quoteRecord) => {
    if (convertingQuote) return
    setConvertingQuote(quoteRecord.href)
    try {
      let quoteId = quoteRecord.id
      if (!quoteId) {
        const quoteResponse = await getQuoteByReferenceNumber({ referenceNumber: quoteRecord.number })
        quoteId = quoteResponse?.data?.quote?.id || quoteResponse?.data?.id
      }
      const response = await transformToWO({ quoteId })
      if (response.status === 200 || response.status === 201) {
        setRefresh?.((prev) => !prev)
        history.push(getWorkOrderSummaryUrl(quoteRecord.number))
        scrollPageTop()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { quoteRecord, path: 'convertQuoteToWorkOrder' } })
      handleCustomError(error)
    } finally {
      setConvertingQuote(null)
    }
  }
  const redirectToNewQuote = () => {
    history.push(REDIRECT_URLS.NEW_QUOTE, { ...formValues })

    // Scroll to top of the page
    scrollPageTop()
  }

  const handleFileDelete = () => {
    setFormValues((prev) => ({
      ...prev,
      recentEstimate: null
    }))

    // Optionally clear any errors related to the file
    setFormErrors((prev) => ({
      ...prev,
      recentEstimate: ''
    }))
  }

  useEffect(() => {
    const fetchReferralSource = async () => {
      const referralListResponse = await getReferralList(ccId)
      if (referralListResponse?.status === 200) {
        const formattedReferralList = referralListResponse.data
          .sort((a, b) => a.company.localeCompare(b.company))
          .map((referralObj) => ({ id: referralObj.code, value: referralObj.company }))
        setReferralSourceList(formattedReferralList)
      }
    }

    fetchReferralSource()
  }, [])

  useEffect(() => {
    if (currentUser) {
      setFormValues((prevState) => ({
        ...prevState,
        updatedBy: currentUser.id
      }))
    }
  }, [currentUser, setFormValues])

  const updateAssignees = async (value) => {
    try {
      setLoading(true)
      const response = await updateSchedularAssignee(ccId, selectedEventId, value)
      if (response.status === 200) {
        fetchAppointmentDetails()
        setRefresh((prev) => !prev)
      }
    } catch (error) {
      Logger.error({
        message: error,
        payload: { value, path: 'updateAssignees' }
      })
    } finally {
      setLoading(false)
    }
  }
  const updateStatus = async (value) => {
    try {
      setLoading(true)
      const response = await updateSchedularStatus(ccId, selectedEventId, value)
      if (response.status === 200) {
        fetchAppointmentDetails()
        setRefresh((prev) => !prev)
      }
    } catch (error) {
      Logger.error({
        message: error,
        payload: { value, path: 'updateStatus' }
      })
      handleCustomError(error)
    } finally {
      setLoading(false)
    }
  }

  const extractMessage = (text) => {
    if (typeof text !== 'string' || !text.trim()) {
      return null
    }

    const messageMatch = text.match(/"message":"(.*?)"/)

    if (messageMatch && messageMatch[1]) {
      return messageMatch[1].replace('Bad arguments sent: ', '')
    }

    return null
  }

  const handleCustomError = (error) => {
    let errorMessage = ''
    if (error?.response?.status === 400) {
      errorMessage = extractMessage(error.response?.data?.message || '')
    }
    addMessage({
      text: errorMessage || error.response?.data?.message
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!editMode) {
      // Transition into edit mode — no API call, clear any prior error.
      setApiError(null)
      setEditMode(true)
      return
    }
    setLoading(true)
    setApiError(null)
    if (formValues.recentEstimate instanceof File) {
      const formData = new FormData()
      formData.append('file', formValues.recentEstimate)
      await createAppointmentEstimate(ccId, selectedEventId, formData)
    }
    const appointmentPayload = AppointmentPayload({
      formValues,
      organisationDetail
    })
    try {
      await updateAppointment(ccId, selectedEventId, appointmentPayload)
      setEditMode(false)
      setRefresh((prev) => !prev)
      cancel()
    } catch (error) {
      Logger.error({
        message: error,
        payload: { appointmentPayload, path: 'handleSubmit' }
      })
      setApiError(extractApiError(error) || 'Failed to save appointment. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = (event, value) => {
    setRefresh((prev) => !prev)
    if (event.type == 'confirm') {
      deleteAppointment()
    }
    if (value && value == 'backdropClick') return
    setOpen(false)
    setCancelAppointmentModal(false)
  }

  const deleteAppointment = async () => {
    try {
      setLoading(true)
      setApiError(null)
      await cancelAppointment(ccId, selectedEventId)

      setRefresh((prev) => !prev)
      cancel()
    } catch (error) {
      Logger.error({
        message: error,
        payload: { selectedEventId, path: 'deleteAppointment' }
      })
      setApiError(extractApiError(error) || 'Failed to cancel appointment. Please try again.')
    } finally {
      setLoading(false)
    }
  }
  const closeModal = () => {
    setCancelAppointmentModal(true)
  }
  const defaultStyle = {
    borderRadius: '4px',
    padding: '0px',
    fontSize: '14px',
    height: '24px',
    minWidth: '120px'
  }
  const handleStatusChange = (event) => {
    const { name, value } = event.target
    if (name === 'status' && formValues && value) {
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value
      }))
      updateStatus(value)
    } else {
      console.warn('Status change failed: Invalid form values or status.')
    }
  }
  const handleAssigneeChange = (event) => {
    const { name, value } = event.target

    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value
    }))

    updateAssignees(value)
  }

  const handlecancel = (event) => {
    setRefresh((prev) => !prev)
    cancel(event)
  }

  const isDisabled = () => {
    return formValues.appointmentDate ? dayjs(dayjs(formValues.appointmentDate).toDate()).diff(dayjs().toDate(), 'day') > -1 : false
  }
  // A past appointment is read-only: Edit and Delete are disabled, and the notice says why.
  const isPastAppointment = !!formValues.appointmentDate && !isDisabled()

  const currentStatusStyle = schedularStatusStyles[formValues.status] || schedularStatusStyles.SCHEDULED
  const apptTitle = formValues.businessName || formValues.fullName || 'Appointment'
  const durationHours = formValues.duration ? formValues.duration / 60 : null
  const durationLabel = durationHours != null ? `${Number.isInteger(durationHours) ? durationHours : durationHours.toFixed(1)} hrs` : '—'

  const mobileDescription = `Confirm cancellation of this appointment. Event will be deleted from calendar, and a cancellation email will be sent once confirmed.`
  const desktopDescription = `Confirm cancellation of this appointment. Event will be deleted from <br /> calendar, and a cancellation email will be sent once confirmed.`
  const referenceLine = [formValues.referenceNo, formValues?.aro && `RO #${formValues.aro}`].filter(Boolean).join(' · ')

  if (editMode) {
    const editFooter = (
      <>
        {availabilities?.overrides_date && <DataOverrideCard availabilities={availabilities} />}
        <AdasBox className='flex justify-end gap-3'>
          <button
            type='button'
            onClick={() => setEditMode(false)}
            className='inline-flex items-center justify-center rounded-lg border border-[#D1D5D8] bg-white px-5 py-2.5 text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            className='inline-flex items-center justify-center rounded-lg bg-sidebar px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            Save Changes
          </button>
        </AdasBox>
      </>
    )
    return (
      <FormModal
        open={isOpen}
        onClose={() => setEditMode(false)}
        title='Edit Appointment'
        icon={Calendar}
        footer={editFooter}
        clientMayEdit
        bodyClassName='pt-4 pb-4'
        mobileFullScreen
        errorMessage={apiError}
        onDismissError={() => setApiError(null)}
      >
        <AppointmentForm
          formValues={formValues}
          handleChange={handleChange}
          assigneeOptions={assigneeOptions}
          type={true}
          setFormValues={setFormValues}
          appointmentId={selectedEventId}
          formErrors={formErrors}
          handleFileDelete={handleFileDelete}
          formValid={formValid}
          setFormValid={setFormValid}
          validationMap={validationMap}
          setAvailabilities={setAvailabilities}
          newTime={newTime}
          formattedDate={formattedDate}
          formEditValues={formEditValues}
        />
      </FormModal>
    )
  }

  const workOrderRecords = linkedRecords.filter((record) => record.stage === 'WORKORDER')
  const quoteRecords = linkedRecords.filter((record) => record.stage === 'QUOTE')
  const invoiceRecords = linkedRecords.filter((record) => record.stage === 'INVOICE')
  // A client user can't create a work order or quote (CAR-3969), so it isn't offered either - not even
  // converting the appointment's own quote. It still gets the View links.
  const canStartNew = !isReadOnly
  const showWorkOrderColumn = workOrderRecords.length > 0 || canStartNew
  // Once a work order exists, only offer the quote column if there is still an open quote to view.
  const showQuoteColumn = (workOrderRecords.length === 0 || quoteRecords.length > 0) && (quoteRecords.length > 0 || canStartNew)

  const renderRecordLink = (record, label) => (
    <button key={record.href} type='button' onClick={() => history.push(record.href)} className={FOOTER_BUTTON_CLASS}>
      <ExternalLink className='h-4 w-4' />
      View {label}
      {record.number ? ` #${record.number}` : ''}
    </button>
  )

  const viewFooter = (
    <>
      {availabilities?.overrides_date && <DataOverrideCard availabilities={availabilities} />}
      {/* - Work order exists: link to it. A quote moved to a work order is no longer open, so
            only the work order link is shown — no Create Quote.
          - Quote exists (no work order): View Quote + Create Work Order, which converts that
            quote via the transform API instead of opening the New Work Order screen.
          - Neither: Create Work Order / Create Quote open their respective new screens. */}
      {(showWorkOrderColumn || showQuoteColumn) && (
        <div className={`grid ${showWorkOrderColumn && showQuoteColumn ? 'grid-cols-2' : 'grid-cols-1'} gap-3 px-0 pb-2 pt-3`}>
          {showWorkOrderColumn && (
            <div className='flex flex-col gap-2'>
              {workOrderRecords.length > 0 ? (
                workOrderRecords.map((record) => renderRecordLink(record, 'Work Order'))
              ) : quoteRecords.length > 0 && canStartNew ? (
                quoteRecords.map((record) => (
                  <button
                    key={record.href}
                    type='button'
                    onClick={() => convertQuoteToWorkOrder(record)}
                    disabled={!!convertingQuote}
                    className={`${FOOTER_BUTTON_CLASS} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    {convertingQuote === record.href ? <Loader2 className='h-4 w-4 animate-spin' /> : <PenLine className='h-4 w-4' />}
                    Create Work Order
                    {quoteRecords.length > 1 && record.number ? ` from #${record.number}` : ''}
                  </button>
                ))
              ) : canStartNew ? (
                <button type='button' onClick={redirectToNewWorkOrder} className={FOOTER_BUTTON_CLASS}>
                  <PenLine className='h-4 w-4' />
                  Create Work Order
                </button>
              ) : null}
            </div>
          )}
          {showQuoteColumn && (
            <div className='flex flex-col gap-2'>
              {quoteRecords.length > 0 ? (
                quoteRecords.map((record) => renderRecordLink(record, 'Quote'))
              ) : canStartNew ? (
                <button type='button' onClick={redirectToNewQuote} className={FOOTER_BUTTON_CLASS}>
                  <Plus className='h-4 w-4' />
                  Create Quote
                </button>
              ) : null}
            </div>
          )}
        </div>
      )}
      {invoiceRecords.length > 0 && (
        <div className='grid grid-cols-1 gap-2 px-0 pb-2'>{invoiceRecords.map((record) => renderRecordLink(record, 'Invoice'))}</div>
      )}
      {isPastAppointment && (
        <div
          role='note'
          data-testid='past-appointment-notice'
          className='mt-2 flex items-start gap-2 rounded-lg border border-[#F59E0B] bg-[#FFFBEB] px-3 py-2 text-[13px] text-[#78350F]'
        >
          <Info className='mt-0.5 h-4 w-4 shrink-0' aria-hidden='true' />
          This appointment is in the past and can no longer be edited or deleted.
        </div>
      )}
      <div className='grid grid-cols-[auto_1fr] gap-3 px-0 pb-2 pt-2'>
        <button
          type='button'
          onClick={closeModal}
          disabled={!isDisabled()}
          className='inline-flex items-center justify-center gap-2 rounded-lg border border-[#FCA5A5] bg-white px-4 py-2.5 text-[13px] font-semibold text-[#DC2626] transition-colors hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:opacity-50'
        >
          <CalendarX className='h-4 w-4' />
          Delete
        </button>
        <button
          type='button'
          onClick={handleSubmit}
          disabled={!isDisabled()}
          className='inline-flex items-center justify-center gap-2 rounded-lg bg-sidebar px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
        >
          <PenLine className='h-4 w-4' />
          Edit Appointment
        </button>
      </div>
    </>
  )

  return (
    <>
      <FormModal
        open={isOpen}
        onClose={handlecancel}
        title='Appointment Details'
        icon={Calendar}
        footer={isDetailsLoading ? null : viewFooter}
        clientMayEdit
        mobileFullScreen
        errorMessage={apiError}
        onDismissError={() => setApiError(null)}
      >
        {isDetailsLoading ? (
          <div className='py-16 text-center'>
            <Loader2 className='mx-auto mb-4 h-10 w-10 animate-spin text-[#1F6FEB]' />
            <div className='text-[15px] font-semibold text-[#0A1628]'>Loading appointment details...</div>
          </div>
        ) : (
          <>
            <div className='mb-4'>
              <div className='text-[20px] font-bold leading-tight text-[#0A1628] truncate'>{apptTitle}</div>
              {referenceLine && <div className='mt-0.5 text-[12px] text-[#6B7280]'>{referenceLine}</div>}
              <div className='mt-3 flex items-center gap-2'>
                <AdasFormControl className='flex-1'>
                  <AdasSelect
                    labelId='AssigneeLabelID'
                    id='AssigneeID'
                    name='assignee'
                    value={formValues.assignee || 'Unassigned'}
                    onChange={handleAssigneeChange}
                    options={assigneeOptions}
                  />
                </AdasFormControl>
                <AdasFormControl className='flex-1'>
                  <AdasSelect
                    labelId='statusOfAppointmentLabelId'
                    style={{
                      borderColor: currentStatusStyle.borderColor,
                      backgroundColor: currentStatusStyle.backgroundColor,
                      color: currentStatusStyle.color
                    }}
                    id='selectItems'
                    value={formValues.status || 'SCHEDULED'}
                    onChange={handleStatusChange}
                    options={statusOptions}
                    name='status'
                  />
                </AdasFormControl>
              </div>
            </div>
            <div className='space-y-3'>
              <div className='rounded-xl border border-[#EBEDF0] bg-white p-4'>
                <div className='mb-3 flex items-center justify-between'>
                  <h3 className='text-[14px] font-bold text-[#0A1628]'>Date &amp; Time</h3>
                  <ChevronDown className='h-4 w-4 text-[#6B7280]' />
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Date</div>
                    <div className='mt-1 text-[13px] text-[#0A1628]'>{formattedDate || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Time</div>
                    <div className='mt-1 text-[13px] text-[#0A1628]'>
                      {startTime} – {endTime}
                    </div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Duration</div>
                    <div className='mt-1 text-[13px] text-[#0A1628]'>{durationLabel}</div>
                  </div>
                </div>
              </div>

              <div className='rounded-xl border border-[#EBEDF0] bg-white p-4'>
                <div className='mb-3 flex items-center justify-between'>
                  <h3 className='text-[14px] font-bold text-[#0A1628]'>Contact Info</h3>
                  <ChevronDown className='h-4 w-4 text-[#6B7280]' />
                </div>
                <div className='space-y-3'>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Business Name</div>
                    <div className='mt-1 text-[13px] text-[#0A1628] break-words'>
                      {formValues?.businessName || formValues?.fullName || '—'}
                    </div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Business Phone</div>
                    <div className='mt-1 text-[13px] text-[#0A1628]'>{formatPhoneNumber(formValues?.businessPhone) || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Mobile Phone</div>
                    <div className='mt-1 text-[13px] text-[#0A1628]'>{formatPhoneNumber(formValues?.phone) || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Primary Email</div>
                    <div className='mt-1 text-[13px] text-[#0A1628] break-words'>{formValues?.primaryEmail || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Secondary Email</div>
                    <div className='mt-1 text-[13px] text-[#0A1628] break-words'>{formValues?.secondaryEmail || '—'}</div>
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Referral</div>
                    <div className='mt-1 text-[13px] text-[#0A1628] break-words'>
                      {referralSourceList.find((referral) => referral.id === formValues?.referral)?.value || '—'}
                    </div>
                  </div>
                </div>
              </div>

              <div className='rounded-xl border border-[#EBEDF0] bg-white p-4'>
                <div className='mb-3 flex items-center justify-between'>
                  <h3 className='text-[14px] font-bold text-[#0A1628]'>Vehicle Info</h3>
                  <ChevronDown className='h-4 w-4 text-[#6B7280]' />
                </div>
                <div className='space-y-3'>
                  <div className='grid grid-cols-2 gap-4'>
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Year</div>
                      <div className='mt-1 text-[13px] text-[#0A1628]'>{formValues?.vehicleYear || '—'}</div>
                    </div>
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Make</div>
                      <div className='mt-1 text-[13px] text-[#0A1628]'>{formValues?.vehicleMake || '—'}</div>
                    </div>
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Model</div>
                      <div className='mt-1 text-[13px] text-[#0A1628]'>{formValues?.vehicleModel || '—'}</div>
                    </div>
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>RO #</div>
                      <div className='mt-1 text-[13px] text-[#0A1628]'>{formValues?.aro || '—'}</div>
                    </div>
                  </div>
                  {formValues.vin && (
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>VIN</div>
                      <div className='mt-1 font-mono text-[13px] tracking-wider text-[#0A1628]'>{formValues.vin}</div>
                    </div>
                  )}
                  <div>
                    <AdasFormControlLabel
                      control={<AdasCheckbox checked={formValues?.pickDeliver ? true : false} name='pickDeliver' />}
                      label='Include Vehicle Pickup & Delivery'
                      disabled={true}
                      color='#21212199'
                      variant='body2'
                    />
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Most Recent Estimate</div>
                    {formValues?.recentEstimate?.id ? (
                      <AppointmentAttachment
                        name='recentEstimate'
                        value={formValues.recentEstimate}
                        onChange={handleChange}
                        onDelete={handleFileDelete}
                        label='Most Recent Estimate'
                        isViewOnly={true}
                      />
                    ) : (
                      <div className='mt-2 flex flex-col items-center justify-center gap-1 rounded-md border border-[#EBEDF0] py-4'>
                        <PdfIcon className='h-6 w-6 text-[#ed6c02]' />
                        <div className='text-[12px] text-[#6B7280]'>No estimate uploaded</div>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Calibration ID Report</div>
                    {formValues?.calibrationFile?.id ? (
                      <AppointmentAttachment
                        name='calibrationFile'
                        value={formValues.calibrationFile}
                        onChange={handleChange}
                        onDelete={handleFileDelete}
                        label='Calibration ID Report'
                        isViewOnly={true}
                      />
                    ) : (
                      <div className='mt-2 flex flex-col items-center justify-center gap-1 rounded-md border border-[#EBEDF0] py-4'>
                        <PdfIcon className='h-6 w-6 text-[#ed6c02]' />
                        <div className='text-[12px] text-[#6B7280]'>No Calibration Id uploaded</div>
                      </div>
                    )}
                  </div>
                  {formValues?.notes && (
                    <div>
                      <div className='text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'>Additional Notes</div>
                      <div className='mt-1 text-[13px] text-[#0A1628] break-words whitespace-pre-wrap'>{formValues.notes}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </FormModal>
      <AdsCustomModal
        title='Send Updates'
        open={open}
        onClose={handleClose}
        description={'Appointment updates saved. Do you want to send the email confirmation?'}
        successButtonTitle={'Send'}
        cancelButtonTitle={'No, Don’t send'}
        cancelButtonConfig={{
          variant: 'outlined'
        }}
      />
      <AdsCustomModal
        title={`Cancel Appointment ${formValues?.referenceNo}`}
        open={cancelAppointmentModal}
        onClose={handleClose}
        description={
          <span
            dangerouslySetInnerHTML={{
              __html: isMobile ? mobileDescription : desktopDescription
            }}
          />
        }
        successButtonTitle={'Confirm'}
        cancelButtonTitle={'No'}
        cancelButtonConfig={{
          variant: 'outlined'
        }}
      >
        <AdasBox mt={3}>
          <AdasBox display='flex' gap={1} mb={1}>
            <AdasTypography variant='body2' color='#21212199'>
              Date & Time:{' '}
            </AdasTypography>
            <AdasTypography color={theme.typography.shaded} variant='body2'>
              {formatDate(formValues?.appointmentDate, DATE_FORMATS.ISO_DATE_STRING)} |
            </AdasTypography>
            <AdasTypography color={theme.typography.shaded} variant='body2'>
              {startTime}
            </AdasTypography>
          </AdasBox>
          <AdasBox display='flex' gap={1}>
            <AdasTypography variant='body2' color='#21212199' className='whitespace-nowrap'>
              Business Name:
            </AdasTypography>
            <AdasTypography color={theme.typography.shaded} variant='body2' className='wordWrap max-w-[80%] sm:max-w-full'>
              {formValues?.businessName || formValues?.fullName}
            </AdasTypography>
          </AdasBox>
        </AdasBox>
      </AdsCustomModal>
    </>
  )
}
